<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import TalkviaCalendarIntegrationAPI from 'dashboard/api/talkviaCalendarIntegration';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const store = useStore();
const getters = useStoreGetters();
const searchQuery = ref('');
const activeCategory = ref('all');
const googleCalendarIntegration = ref(null);
const uiFlags = getters['integrations/getUIFlags'];
const currentUser = getters.getCurrentUser;
const accountId = getters.getCurrentAccountId;
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const currentAccount = computed(
  () =>
    currentUser.value?.accounts?.find(
      account => Number(account.id) === Number(accountId.value)
    ) || {}
);

const hasAccountFeature = feature =>
  Boolean(
    isFeatureEnabledonAccount.value?.(accountId.value, feature) ||
      currentAccount.value?.features?.[feature]
  );

const hiddenIntegrationIds = new Set([
  'dashboard_apps',
  'corban',
  'dialogflow',
  'dyte',
  'google_translate',
  'leadsquared',
]);

const featuredIntegrationIds = ['banking', 'erp'];
const priorityIntegrationIds = [
  'google_calendar',
  'email_inbox',
  'openai',
  'webhook',
];

const integrationCategories = {
  banking: 'operations',
  erp: 'operations',
  google_calendar: 'productivity',
  email_inbox: 'communication',
  webhook: 'automation',
  openai: 'automation',
  slack: 'communication',
  shopify: 'operations',
  notion: 'productivity',
  linear: 'productivity',
};

const localizedIntegrationCopy = {
  banking: {
    name: 'Integrações bancárias',
    description:
      'Centralize consultas, propostas e retornos das instituições financeiras habilitadas.',
  },
  erp: {
    name: 'IXCSoft e MkAuth',
    description:
      'Conecte rotinas operacionais, financeiras e de atendimento dos seus ERPs.',
  },
  google_calendar: {
    name: 'Google Agenda',
    description:
      'Sincronize compromissos e crie links do Google Meet direto pela agenda.',
  },
  email_inbox: {
    name: 'Caixas de e-mail',
    description:
      'Conecte Gmail, Outlook, Microsoft 365 ou provedores IMAP/SMTP.',
  },
  openai: {
    name: 'OpenAI',
    description:
      'Configure os recursos de IA usados nas respostas, análises e automações.',
  },
  webhook: {
    name: 'Webhooks',
    description:
      'Envie eventos da plataforma para sistemas externos em tempo real.',
  },
};

const erpFeatureFlags = [
  FEATURE_FLAGS.TALKVIA_PROVIDER_IXC,
  FEATURE_FLAGS.TALKVIA_PROVIDER_MKAUTH,
];

const hasAnyAccountFeature = features => features.some(hasAccountFeature);

const canShowLocalIntegration = id => {
  if (id === 'erp') return hasAnyAccountFeature(erpFeatureFlags);
  if (id === 'banking') return hasAccountFeature(FEATURE_FLAGS.TALKVIA_CORBAN);
  return true;
};

const integrationList = computed(
  () => getters['integrations/getAppIntegrations'].value
);

const localIntegrationFallbacks = computed(() => [
  {
    id: 'google_calendar',
    name: 'Google Agenda',
    description:
      'Sincronize eventos da agenda da Talkvia com o Google Agenda e gere links do Google Meet.',
    enabled: Boolean(
      googleCalendarIntegration.value?.active ||
        googleCalendarIntegration.value?.has_credentials
    ),
  },
  {
    id: 'email_inbox',
    name: 'Caixas de e-mail',
    description:
      'Conecte Gmail, Outlook/Hotmail, Microsoft 365 ou qualquer provedor via IMAP/SMTP.',
    enabled: false,
    actionRoute: {
      name: 'settings_inboxes_page_channel',
      params: { accountId: accountId.value, sub_page: 'email' },
    },
  },
]);

const getIntegrationsById = (list, ids) =>
  ids.map(id => list.find(item => item.id === id)).filter(Boolean);

const integrationStatus = item => {
  if (item.error || item.status === 'error') return 'error';
  if (item.pending || item.status === 'pending') return 'pending';
  return item.enabled ? 'active' : 'disabled';
};

const visibleIntegrationList = computed(() => {
  const integrationIds = new Set(integrationList.value.map(item => item.id));
  const fallbackItems = localIntegrationFallbacks.value.filter(
    item => !integrationIds.has(item.id)
  );

  return [...integrationList.value, ...fallbackItems]
    .filter(item => {
      if (hiddenIntegrationIds.has(item.id)) return false;
      if (!canShowLocalIntegration(item.id)) return false;

      return true;
    })
    .map(item => ({
      ...item,
      ...localizedIntegrationCopy[item.id],
      category: integrationCategories[item.id] || 'other',
    }));
});

const categoryTabs = computed(() => {
  const categories = [
    { value: 'all', label: 'Todas', icon: 'i-lucide-layout-grid' },
    { value: 'operations', label: 'Operação', icon: 'i-lucide-workflow' },
    {
      value: 'communication',
      label: 'Comunicação',
      icon: 'i-lucide-messages-square',
    },
    {
      value: 'productivity',
      label: 'Produtividade',
      icon: 'i-lucide-calendar-check-2',
    },
    { value: 'automation', label: 'Automação e IA', icon: 'i-lucide-bot' },
  ];

  return categories.map(category => ({
    ...category,
    count:
      category.value === 'all'
        ? visibleIntegrationList.value.length
        : visibleIntegrationList.value.filter(
            item => item.category === category.value
          ).length,
  }));
});

const integrationSummary = computed(() => ({
  total: visibleIntegrationList.value.length,
  active: visibleIntegrationList.value.filter(
    item => integrationStatus(item) === 'active'
  ).length,
  attention: visibleIntegrationList.value.filter(item =>
    ['pending', 'error'].includes(integrationStatus(item))
  ).length,
}));

const searchIntegrations = list => {
  const categoryFiltered =
    activeCategory.value === 'all'
      ? list
      : list.filter(item => item.category === activeCategory.value);
  const query = searchQuery.value.trim();
  if (!query) return categoryFiltered;
  return picoSearch(categoryFiltered, query, ['name', 'description']);
};

const featuredIntegrationList = computed(() =>
  getIntegrationsById(visibleIntegrationList.value, featuredIntegrationIds)
);

const standardIntegrationList = computed(() => {
  const featuredIds = new Set(featuredIntegrationIds);
  const standardItems = visibleIntegrationList.value.filter(
    item => !featuredIds.has(item.id)
  );
  const priorityItems = getIntegrationsById(
    standardItems,
    priorityIntegrationIds
  );
  const priorityIds = new Set(priorityItems.map(item => item.id));

  return [
    ...priorityItems,
    ...standardItems.filter(item => !priorityIds.has(item.id)),
  ];
});

const filteredFeaturedIntegrationList = computed(() =>
  searchIntegrations(featuredIntegrationList.value)
);

const filteredStandardIntegrationList = computed(() =>
  searchIntegrations(standardIntegrationList.value)
);

const hasNoSearchResults = computed(
  () =>
    (searchQuery.value || activeCategory.value !== 'all') &&
    !filteredFeaturedIntegrationList.value.length &&
    !filteredStandardIntegrationList.value.length
);

const loadGoogleCalendarIntegration = async () => {
  try {
    const { data } = await TalkviaCalendarIntegrationAPI.get();
    googleCalendarIntegration.value = data;
  } catch {
    googleCalendarIntegration.value = null;
  }
};

onMounted(() => {
  store.dispatch('integrations/get');
  loadGoogleCalendarIntegration();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto px-4 py-5 sm:px-6">
        <section class="mx-auto mb-5 max-w-[1180px]">
          <div
            class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
          >
            <div
              class="relative grid gap-5 overflow-hidden border-b border-n-weak bg-n-solid-1 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
            >
              <div
                class="pointer-events-none absolute inset-y-0 left-0 w-1 bg-n-brand"
              />
              <div class="max-w-3xl">
                <div
                  class="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-n-blue-10"
                >
                  <span class="i-lucide-plug-zap size-4" />
                  Central de integrações
                </div>
                <h1 class="text-2xl font-semibold text-n-slate-12">
                  Conecte sua operação
                </h1>
                <p class="mt-2 max-w-2xl text-sm leading-6 text-n-slate-10">
                  Encontre, acompanhe e configure em um só lugar os serviços
                  usados pela sua equipe.
                </p>
              </div>
              <div class="grid grid-cols-3 gap-2">
                <div class="integration-summary-card">
                  <span class="text-lg font-semibold text-n-slate-12">{{
                    integrationSummary.total
                  }}</span>
                  <small class="text-[10px] uppercase text-n-slate-9">
                    Total
                  </small>
                </div>
                <div class="integration-summary-card">
                  <span class="text-lg font-semibold text-n-teal-10">{{
                    integrationSummary.active
                  }}</span>
                  <small class="text-[10px] uppercase text-n-slate-9">
                    Ativas
                  </small>
                </div>
                <div class="integration-summary-card">
                  <span class="text-lg font-semibold text-n-amber-10">{{
                    integrationSummary.attention
                  }}</span>
                  <small class="text-[10px] uppercase text-n-slate-9">
                    Atenção
                  </small>
                </div>
              </div>
            </div>

            <div class="grid gap-4 p-4">
              <Input
                v-model="searchQuery"
                type="search"
                :placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
                custom-input-class="!pl-10"
              >
                <template #prefix>
                  <span
                    class="i-lucide-search pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-9"
                  />
                </template>
              </Input>

              <div class="flex max-w-full gap-2 overflow-x-auto pb-1">
                <button
                  v-for="category in categoryTabs"
                  :key="category.value"
                  type="button"
                  class="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition"
                  :class="
                    activeCategory === category.value
                      ? 'border-n-brand bg-n-brand/10 text-n-brand shadow-sm'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-10 hover:border-n-strong hover:bg-n-alpha-2 hover:text-n-slate-12'
                  "
                  @click="activeCategory = category.value"
                >
                  <span :class="category.icon" class="size-4" />
                  {{ category.label }}
                  <span
                    class="rounded-full bg-n-alpha-2 px-1.5 py-0.5 text-[10px]"
                  >
                    {{ category.count }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <span
          v-if="hasNoSearchResults"
          class="mx-auto flex max-w-[1180px] flex-1 items-center justify-center rounded-xl border border-n-weak bg-n-solid-2 py-16 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div v-else class="mx-auto flex max-w-[1180px] flex-col gap-6">
          <div
            v-if="filteredFeaturedIntegrationList.length"
            class="flex flex-col gap-3"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-star size-4 text-n-amber-9" />
              <h3
                class="text-sm font-semibold text-n-slate-11 uppercase tracking-wide"
              >
                Em destaque
              </h3>
            </div>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <IntegrationItem
                v-for="item in filteredFeaturedIntegrationList"
                :id="item.id"
                :key="item.id"
                :logo="item.logo"
                :name="item.name"
                :description="item.description"
                :enabled="item.enabled"
                :status="integrationStatus(item)"
                :action-route="item.actionRoute"
                :action-url="item.actionUrl"
              />
            </div>
          </div>
          <div
            v-if="filteredStandardIntegrationList.length"
            class="flex flex-col gap-3 border-t border-n-weak pt-4"
          >
            <div class="flex items-center gap-2 pt-4">
              <span class="i-lucide-puzzle size-4 text-n-slate-10" />
              <h3
                class="text-sm font-semibold text-n-slate-11 uppercase tracking-wide"
              >
                Todas as integrações
              </h3>
            </div>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <IntegrationItem
                v-for="item in filteredStandardIntegrationList"
                :id="item.id"
                :key="item.id"
                :logo="item.logo"
                :name="item.name"
                :description="item.description"
                :enabled="item.enabled"
                :status="integrationStatus(item)"
                :action-route="item.actionRoute"
                :action-url="item.actionUrl"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.integration-summary-card {
  @apply grid min-w-20 place-items-center rounded-xl border border-n-weak bg-n-solid-1/80 px-3 py-2.5 shadow-sm;
}
</style>
